const normalizeString = (value) => String(value || "").trim();
const normalizeSortOrder = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const normalizePositiveInteger = (value, fallback = 1) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) {
    return fallback;
  }

  return Math.max(1, Math.floor(parsed));
};

export const buildFallbackAvailability = (preco) => ({
  tracked: false,
  available: preco !== "Em breve",
  quantity: null,
  label: preco === "Em breve" ? "em breve" : null,
  tone: "neutral",
});

export const buildFallbackOptionAvailability = () => ({
  tracked: false,
  available: true,
  quantity: null,
  label: null,
  tone: "neutral",
});

export const normalizeCustomizationGroups = (groups) =>
  (Array.isArray(groups) ? groups : []).map((group) => ({
    id: normalizeString(group?.id),
    label: normalizeString(group?.label),
    helperText: normalizeString(group?.helperText),
    type:
      group?.type === "multiple"
        ? "multiple"
        : group?.type === "quantity"
          ? "quantity"
          : "single",
    required: Boolean(group?.required),
    selectionLimit:
      group?.type === "quantity"
        ? normalizePositiveInteger(
            group?.selectionLimit ?? group?.selectionCount ?? group?.quantity,
            1,
          )
        : 1,
    options: (Array.isArray(group?.options) ? group.options : []).map(
      (option) => ({
        id: normalizeString(option?.id),
        label: normalizeString(option?.label),
        description: normalizeString(option?.description || option?.helperText),
        priceDelta: Number(option?.priceDelta || 0),
        imageUrl: normalizeString(
          option?.imageUrl || option?.image || option?.imagem,
        ),
        availability: option?.availability || buildFallbackOptionAvailability(),
      }),
    ),
  }));

export const normalizeWeightLabel = (value) => normalizeString(value);

export const buildMenuCategories = (rawCategories) => {
  const categories = Array.isArray(rawCategories) ? rawCategories : [];

  return categories
    .filter((category) => !category?.hidden)
    .map((category, categoryIndex) => ({
      id: normalizeString(category?.id),
      label: normalizeString(category?.label || category?.id),
      sortOrder: normalizeSortOrder(category?.sortOrder, categoryIndex),
      data: (Array.isArray(category?.items) ? category.items : [])
        .map((item, itemIndex) => ({
          ...item,
          id: normalizeString(item?.id),
          nome: normalizeString(item?.nome),
          weightLabel: normalizeWeightLabel(item?.weightLabel),
          descricao: normalizeString(item?.descricao),
          preco: normalizeString(item?.preco),
          imagem: normalizeString(item?.imagem),
          productType: normalizeString(item?.productType),
          sortOrder: normalizeSortOrder(item?.sortOrder, itemIndex),
          availability:
            item?.availability || buildFallbackAvailability(item?.preco),
          minimumLeadTimeMinutes: Math.max(
            0,
            Math.floor(Number(item?.minimumLeadTimeMinutes) || 0),
          ),
          customizationGroups: normalizeCustomizationGroups(
            item?.customizationGroups,
          ),
        }))
        .sort((left, right) => left.sortOrder - right.sortOrder),
    }))
    .filter((category) => category.id && category.label)
    .sort((left, right) => left.sortOrder - right.sortOrder);
};

export const getProductType = (product) => {
  const explicitType = normalizeString(product?.productType);
  if (explicitType) {
    return explicitType;
  }

  const productId = normalizeString(product?.id);
  if (productId.startsWith("cx")) return "caixinha";
  if (productId.startsWith("l")) return "lata";
  if (productId.startsWith("t")) return "tabuleiro";
  return "simple";
};

export const getPackagingLabel = (productOrType) => {
  const productType =
    typeof productOrType === "string"
      ? productOrType
      : getProductType(productOrType);

  if (productType === "lata") return "Lata";
  if (productType === "caixinha") return "Caixinha";
  if (productType === "tabuleiro") return "Tabuleiro";
  return "";
};

export const isStampGroup = (group) => {
  const raw =
    `${normalizeString(group?.id || group?.groupId)} ${normalizeString(
      group?.label || group?.groupLabel,
    )}`.toLowerCase();
  return raw.includes("estampa");
};

export const getStampGroup = (product) =>
  (Array.isArray(product?.customizationGroups)
    ? product.customizationGroups
    : []
  ).find((group) => isStampGroup(group)) || null;

export const getStampOptions = (product) =>
  getStampGroup(product)?.options || [];

export const isOptionUnavailable = (option) =>
  option?.availability?.available === false;

export const getAvailableOptions = (group) =>
  (Array.isArray(group?.options) ? group.options : []).filter(
    (option) => !isOptionUnavailable(option),
  );

export const hasAvailableOptions = (group) =>
  getAvailableOptions(group).length > 0;

export const getAvailableStampOptions = (product) =>
  getStampOptions(product).filter((option) => !isOptionUnavailable(option));

export const hasVisualOptions = (group) =>
  (Array.isArray(group?.options) ? group.options : []).some((option) =>
    normalizeString(option?.imageUrl),
  );

export const getCustomizationSummaryLines = (item) =>
  (Array.isArray(item?.customizationSummary) ? item.customizationSummary : [])
    .map((line) => normalizeString(line))
    .filter(Boolean);

export const getSelectedOptionLabels = (item, matcher) =>
  (Array.isArray(item?.customizations) ? item.customizations : [])
    .filter((group) => matcher(group))
    .flatMap((group) =>
      (Array.isArray(group?.options) ? group.options : [])
        .map((option) => normalizeString(option?.label))
        .filter(Boolean),
    );

export const getSelectedStampLabel = (item) => {
  const selectedLabel = getSelectedOptionLabels(item, isStampGroup)[0];

  if (selectedLabel) {
    return selectedLabel;
  }

  const summaryLine = getCustomizationSummaryLines(item).find((line) =>
    isStampGroup({ label: line.split(":")[0] }),
  );

  return summaryLine ? summaryLine.split(":").slice(1).join(":").trim() : "";
};

export const getSelectedStampOption = (item) =>
  (Array.isArray(item?.customizations) ? item.customizations : [])
    .filter((group) => isStampGroup(group))
    .flatMap((group) => (Array.isArray(group?.options) ? group.options : []))
    .find((option) => normalizeString(option?.label)) || null;

export const getSelectedStampImage = (item) =>
  (Array.isArray(item?.customizations) ? item.customizations : [])
    .filter((group) => isStampGroup(group))
    .flatMap((group) => (Array.isArray(group?.options) ? group.options : []))
    .map((option) => normalizeString(option?.imageUrl))
    .find(Boolean) || "";

export const getNonStampCustomizationSummaryLines = (item) =>
  getCustomizationSummaryLines(item).filter(
    (line) => !isStampGroup({ label: line.split(":")[0] }),
  );
